import { MessageSquarePlus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { FileDropOverlay } from '@/components/ui/FileDropOverlay'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  useAgentConversation,
  useAgentConversations,
  useCreateAgentConversation,
} from '@/hooks/useAgentConversation'
import { cn } from '@/lib/utils'
import { ChatPanel } from '@/routes/Private/App/Internal/AgentChat/ChatPanel'
import { ConversationDropdown } from './ConversationDropdown'
import { DocumentPreviewPane } from './DocumentPreviewPane'
import type { PageContextValue } from './pageContext'
import type { AgentPanelState } from './useAgentPanelState'
import { useDocumentPreview } from './useDocumentPreview'

interface AgentSidePanelProps {
  panel: AgentPanelState
  pageContext: PageContextValue | null
  /** Header title, and the base of the panel's accessible names. */
  title: string
  /** Rendered left of the title. */
  icon: React.ReactNode
  /** Second line of the empty state, above the New chat button. */
  emptyStateDescription: string
  /**
   * Gap left under the panel for fixed page chrome — the internal hub's comms
   * bar. Tailwind class, so it can't be a computed value.
   */
  bottomClassName?: string
  /**
   * Widen the panel and show the documents this agent produces beside the
   * chat. Internal only — the bytes come from a Cai-admin endpoint.
   */
  showDocumentPreview?: boolean
}

/**
 * An agent chat as a right-hand panel, available on every page of its section.
 *
 * A plain fixed panel rather than the shared `DrawerWithCloseButton`: vaul
 * doesn't forward `modal` to the Radix dialog underneath, so every drawer locks
 * the body — `pointer-events: none`, `aria-hidden` on the rest of the app, and
 * a scroll lock. The user has to keep working and navigating while this is
 * open, which is what makes the page context follow them, so it can't be a
 * dialog.
 *
 * Stays mounted once opened, so the thread, its draft, and any turn it is
 * streaming survive being closed and reopened.
 *
 * Which agent it talks to comes entirely from the surrounding
 * `AgentKindProvider` — everything here is the same for both.
 */
export function AgentSidePanel({
  panel,
  pageContext,
  title,
  icon,
  emptyStateDescription,
  bottomClassName = 'bottom-0',
  showDocumentPreview = false,
}: AgentSidePanelProps) {
  const { open, close, activeId, setActiveId } = panel
  const { conversations, isLoading } = useAgentConversations()
  const createMutation = useCreateAgentConversation()
  const [everOpened, setEverOpened] = useState(false)
  const { previewDocuments, setFilledDocuments, pickDocument, closePreview } = useDocumentPreview(activeId)
  // The whole panel is the drop zone — header and document pane included.
  const dropZoneRef = useRef<HTMLElement>(null)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  // A phone has no room for a second pane beside a chat that already fills it.
  const isMobile = useIsMobile()
  const canPreview = showDocumentPreview && !isMobile
  const previewOpen = canPreview && previewDocuments.length > 0

  useEffect(() => {
    if (open) setEverOpened(true)
  }, [open])

  // Resume the most recent thread rather than creating a chat nobody asked for.
  useEffect(() => {
    if (!open || activeId || isLoading || conversations.length === 0) return
    setActiveId(conversations[0].id)
  }, [open, activeId, isLoading, conversations, setActiveId])

  // A selection restored from sessionStorage, or deleted in another tab, would
  // otherwise strand the panel on "Conversation not found" with no way back.
  const { conversation: active, isLoading: activeLoading } = useAgentConversation(activeId)
  useEffect(() => {
    if (!activeId || activeLoading || active) return
    setActiveId(undefined)
  }, [activeId, activeLoading, active, setActiveId])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, close])

  const activeTitle = active?.title ?? conversations.find((c) => c.id === activeId)?.title

  const handleNew = () => {
    createMutation.mutate(undefined, { onSuccess: (conv) => setActiveId(conv.id) })
  }

  return (
    <aside
      ref={dropZoneRef}
      role="complementary"
      aria-label={title}
      data-slot="agent-panel"
      // Keeps the off-screen panel untabbable without unmounting the conversation.
      inert={!open}
      className={cn(
        // Past 100%: the close button hangs off the left edge and would peek when closed.
        // z-210 outranks the page's own popovers and tooltips (z-200); index.css drops it under an open modal.
        'bg-background fixed top-0 right-0 z-210 flex w-[95vw] max-w-[98vw] transition-[transform,width] duration-300',
        // The chat column is fixed, so all the extra width lands on the document.
        previewOpen ? 'sm:w-[1680px]' : 'sm:w-[620px]',
        bottomClassName,
        open ? 'translate-x-0' : 'translate-x-[calc(100%+2rem)]'
      )}
      style={{ boxShadow: '-4px 0 6px 0 rgba(0, 0, 0, 0.05)' }}
    >
      <button
        type="button"
        onClick={close}
        aria-label={`Close ${title.toLowerCase()}`}
        className="bg-background absolute top-5 -left-5 flex size-9 cursor-pointer items-center justify-center rounded-full"
        style={{ boxShadow: '-4px 0 6px 0 rgba(0, 0, 0, 0.05)' }}
      >
        <X className="text-neutral-400" />
      </button>

      {previewOpen && (
        <DocumentPreviewPane docs={previewDocuments} onClose={closePreview} className="flex-1" />
      )}

      <div
        className={cn(
          'flex h-full min-h-0 flex-col gap-3 px-5 py-6',
          previewOpen ? 'w-[620px] shrink-0' : 'w-full'
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {icon}
            <span className="truncate text-lg font-semibold">{title}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ConversationDropdown
              activeId={activeId}
              activeTitle={activeTitle}
              onSelect={setActiveId}
              onDeleted={(id) => id === activeId && setActiveId(undefined)}
            />
            <Button
              size="icon-sm"
              variant="outline"
              onClick={handleNew}
              disabled={createMutation.isPending}
              aria-label="New chat"
              title="New chat"
            >
              <MessageSquarePlus className="size-4" />
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1">
          {!everOpened ? null : activeId ? (
            // Withheld while closed: else every entity page resolves a chip label.
            <ChatPanel
              conversationId={activeId}
              pageContext={open ? pageContext : null}
              handedOverPrompt={panel.handedOverPrompt}
              onHandedOverPromptSent={() => panel.setHandedOverPrompt(undefined)}
              onFilledDocuments={canPreview ? setFilledDocuments : undefined}
              onPreviewDocument={canPreview ? pickDocument : undefined}
              dropZoneRef={dropZoneRef}
              onDraggingChange={setIsDraggingFiles}
              chromeless
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="space-y-1">
                <div className="text-lg font-semibold">Start a chat</div>
                <div className="text-muted-foreground text-sm">{emptyStateDescription}</div>
              </div>
              <Button onClick={handleNew} loading={createMutation.isPending ? 'Starting…' : undefined}>
                <MessageSquarePlus className="size-4" />
                New chat
              </Button>
            </div>
          )}
        </div>
      </div>

      {isDraggingFiles && <FileDropOverlay />}
    </aside>
  )
}
